/* eslint-disable no-unused-vars */
import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Button, Container, IconButton, Skeleton, Stack,
} from '@mui/material';
// import { ArrowDownwardRounded, ArrowUpwardRounded } from '@mui/icons-material';
import moment from 'moment';
import { AccessTimeRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import AlertContext from '../../../../Context/AlertContext';
import Elements from '../../../Elements';
import { deleteAddOn, getAddOns } from '../../../../Redux/Models/master';
import { getUserData, setUserData } from '../../../../Utils/storages';
import Images from '../../../../Configs/images';
import ROUTES from '../../../../Configs/routes';
import ConfirmationContext from '../../../../Context/ConfirmationContext';
import { formatNumToCurrency } from '../../../../Utils/helpers';

export default function Component({
  classes, isLoadingAddOns,
}) {
  const user = getUserData();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { openAlert } = useContext(AlertContext);
  const { openConfirmation, closeConfirmation } = useContext(ConfirmationContext);
  const {
    addOns, updatedAddons, message, error,
  } = useSelector((state) => state.master);

  const myAddons = user.addons || [];

  const quotaLeft = user.quota || 0;
  const totalQuota = user.total_quota || 0;

  useEffect(() => {
    if (error) {
      openAlert({
        message: `Something went wrong. ${message}`,
        severity: 'error',
      });
    }
  }, [error]);

  useEffect(() => {
    if (updatedAddons) {
      openAlert({
        message,
        severity: 'success',
      });
    }
  }, [updatedAddons]);

  useEffect(() => {
    dispatch(getAddOns(user.id));
  }, []);

  const handleDeleteAddon = (addon) => {
    if (addon.extra <= quotaLeft) {
      openConfirmation({
        title: 'Unsubscribe this addon?',
        desc: 'Are you sure want to stop this addon subscription?',
        confirmText: 'Yes, Unsub',
        handleAgree: async () => {
          try {
            await dispatch(deleteAddOn(user.id, {
              subscription_id: addon.subscription_id,
            }));
            closeConfirmation();
            const newAddons = myAddons.filter((ad) => ad.subscription_id !== addon.subscription_id);
            setUserData({
              ...user,
              addons: newAddons,
              total_quota: totalQuota - addon.extra,
            });
          } catch (err) {
            openAlert({
              title: 'Unsubscribe failed',
              message: 'Something went wrong, please try again!',
              severity: 'error',
            });
          }
        },
        handleClose: () => closeConfirmation(),
      });
    } else {
      openAlert({
        title: 'Unsubscribe denied',
        message: 'Sorry, you have used the extra storage from this addon',
        severity: 'error',
      });
    }
  };

  return (
    <Elements.PageBase navigate={navigate} noNav>
      <div className={`${classes.root} no-scrollbar`}>
        <Elements.Navigator title={t('storageInfoPage.title')} onBack={() => navigate(ROUTES.home())} />
        <Container>
          <Box mt={3} fontSize={14}>
            {t('storageInfoPage.desc')}
          </Box>

          <Box display="flex" gap={1} mt={3}>
            <img alt="extra storage" width="20px" height="20px" src={Images.OenoStorage} />
            <Box>
              <Box fontSize={16} fontWeight="700" flex={1}>
                {t('storageInfoPage.storageDetails')}
              </Box>
              <Box className="mute">
                {t('storageInfoPage.availStorage', { count: totalQuota })}
              </Box>
            </Box>
          </Box>

          <Stack direction="column" gap={1.5} mt={2}>
            <Box
              border="1px solid #E5E5E5"
              py={1}
              px={1.5}
              borderRadius="10px"
              display="flex"
              justifyContent="space-between"
              gap={1}
            >
              <img alt="extra storage" width="20px" height="20px" src={Images.IconSubscription} />
              <Box flex={1}>
                <Box fontSize={12} className="mute">
                  Personal Package
                </Box>
                <Box fontSize={14} fontWeight="700" my={0.5}>
                  6 Storages
                </Box>
                <Box fontSize={10} className="very-mute" display="flex" gap="5px" alignItems="center">
                  <AccessTimeRounded sx={{ fontSize: 14 }} />
                  Lifetime
                </Box>
              </Box>
            </Box>
            {myAddons.map((addon) => (
              <Box
                key={addon.created_at}
                border="1px solid #E5E5E5"
                py={1}
                px={1.5}
                borderRadius="10px"
                display="flex"
                justifyContent="space-between"
                gap={1}
              >
                <img alt="extra storage" width="20px" height="20px" src={Images.OenoStorage} />
                <Box flex={1}>
                  <Box fontSize={12} className="mute">
                    {t('storageInfoPage.extraStorage')}
                  </Box>
                  <Box fontSize={14} fontWeight="700" my={0.5}>
                    {t('storageInfoPage.storages', { count: addon.extra })}
                  </Box>
                  <Box fontSize={10} className="very-mute" display="flex" gap="5px" alignItems="center">
                    <AccessTimeRounded sx={{ fontSize: 14 }} />
                    {moment(addon.created_at).format('DD MMM YYYY')}
                  </Box>
                </Box>
                <Box>
                  <IconButton sx={{ pt: 0, pr: 0 }} onClick={() => handleDeleteAddon(addon)}>
                    <img src={Images.Trash} alt="delete" />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Stack>

          <Box display="flex" gap={1} mt={3} alignItems="center">
            <img alt="extra storage" width="20px" height="20px" src={Images.OenoStorage} />
            <Box fontSize={16} fontWeight="700" flex={1}>
              {t('storageInfoPage.needMore')}
            </Box>
          </Box>

          <Stack direction="column" gap={1.5} mt={2}>
            {isLoadingAddOns ? (
              <Box className="mt-3">
                {new Array(2).fill(1).map((e, index) => (
                  <Box className="tracking-box" key={index} mb={2}>
                    <div className="flex space-between mt-2">
                      <div style={{ width: '65%' }}>
                        <Skeleton variant="text" width="100%" />
                        <Skeleton variant="text" width="60%" />
                      </div>
                      <div>
                        <Skeleton variant="text" width={80} />
                      </div>
                    </div>
                  </Box>
                ))}
              </Box>
            ) : addOns.map((addon) => (
              <Box
                key={addon.id}
                border="1px solid #E5E5E5"
                py={1}
                px={1.5}
                borderRadius="10px"
                display="flex"
                justifyContent="space-between"
              >
                <Box>
                  <Box display="flex" alignItems="center" gap={0.5} fontSize={14} fontWeight="700">
                    <img alt="extra storage" src={Images.BoxOutlined} width="11px" height="11px" />
                    {t('storageInfoPage.storages', { count: addon.extra })}
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5} fontSize={14} fontWeight="700" mt={0.5}>
                    <img alt="euro" src={Images.Euro} width="11px" height="11px" />
                    {formatNumToCurrency(Number(addon.price))}
                  </Box>
                </Box>
                <Box>
                  <Button variant="outlined" className="btn-outlined" onClick={() => navigate(ROUTES.extraStorageConfirm(), { state: addon })}>{t('storageInfoPage.buy')}</Button>
                </Box>
              </Box>
            ))}

          </Stack>
          {/* </Container> */}

        </Container>
      </div>
    </Elements.PageBase>
  );
}
